import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';

export default function Navbar() {
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    // Handle keyboard navigation for dropdown
    const handleDropdownKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setDropdownOpen(false);
            dropdownButtonRef.current?.focus();
        } else if (e.key === 'ArrowDown' && !dropdownOpen) {
            e.preventDefault();
            setDropdownOpen(true);
        }
    };

    // Main public tabs
    const mainTabs = [
        { to: '/signups', label: '📝 Fall 2026 Signups', highlight: true },
        { to: '/games', label: '🎮 Games & Roles' },
        { to: '/schedule', label: '📅 Schedule' },
        { to: '/trophies', label: '🏆 State Finals & Trophies' },
    ];

    // More links dropdown
    const moreLinks = [
        { to: '/handbook', label: '📚 Season Handbook' },
        { to: '/conduct', label: '📜 Code of Conduct' },
        { to: '/parents', label: '👨‍👩‍👧 Parents Guide' },
        { to: '/records', label: '📊 Player Records' },
    ];

    return (
        <nav className="glass border-b border-secondary-700 sticky top-0 z-50" aria-label="Main navigation">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group shrink-0" aria-label="Peck Pirates Esports - Home">
                        <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center group-hover:bg-primary-600 transition-colors shadow-md">
                            <span className="text-white font-display font-bold text-xl" aria-hidden="true">🏴‍☠️</span>
                        </div>
                        <div>
                            <span className="font-display font-bold text-lg text-white block leading-tight">
                                Peck Pirates
                            </span>
                            <span className="text-xs text-primary-400 font-semibold tracking-wider uppercase">
                                Esports • Fall 2026
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation - Main Tabs */}
                    <div className="hidden lg:flex items-center gap-1" role="menubar">
                        {mainTabs.map(tab => (
                            <Link
                                key={tab.to}
                                to={tab.to}
                                role="menuitem"
                                aria-current={location.pathname === tab.to ? 'page' : undefined}
                                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === tab.to
                                    ? 'bg-primary-500 text-white shadow-sm'
                                    : tab.highlight
                                        ? 'bg-primary-500/20 text-primary-300 hover:bg-primary-500/30 border border-primary-500/30'
                                        : 'text-secondary-300 hover:bg-secondary-800 hover:text-white'
                                    }`}
                            >
                                {tab.label}
                            </Link>
                        ))}

                        {/* More dropdown */}
                        <div className="relative" ref={dropdownRef} onKeyDown={handleDropdownKeyDown}>
                            <button
                                ref={dropdownButtonRef}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="px-3.5 py-2 rounded-lg text-sm font-medium text-secondary-300 hover:bg-secondary-800 hover:text-white flex items-center gap-1"
                                aria-expanded={dropdownOpen}
                                aria-haspopup="menu"
                                aria-controls="more-menu"
                            >
                                More <span aria-hidden="true" className="text-xs">{dropdownOpen ? '▲' : '▼'}</span>
                            </button>
                            {dropdownOpen && (
                                <div
                                    id="more-menu"
                                    role="menu"
                                    className="absolute right-0 top-full mt-1 w-52 bg-secondary-800 border border-secondary-700 rounded-lg shadow-xl py-1 z-50 animate-fade-in"
                                >
                                    {moreLinks.map((link, index) => (
                                        <Link
                                            key={link.to}
                                            to={link.to}
                                            role="menuitem"
                                            tabIndex={0}
                                            onClick={() => setDropdownOpen(false)}
                                            className={`block px-4 py-2.5 text-sm text-secondary-200 hover:bg-secondary-700 hover:text-white transition-colors ${index === 0 ? 'rounded-t-lg' : ''
                                                } ${index === moreLinks.length - 1 ? 'rounded-b-lg' : ''}`}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Direct Signup Action Button */}
                    <div className="flex items-center gap-2">
                        <a
                            href="https://forms.gle/2GvpB888G5xRci1S9"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-yellow-400 hover:bg-yellow-300 text-primary-950 font-bold px-4 py-2 rounded-lg text-sm transition-all shadow-md hover:shadow-yellow-400/20 flex items-center gap-1.5"
                        >
                            <span>Sign Up Form</span>
                            <span aria-hidden="true" className="text-xs">↗</span>
                        </a>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMenuOpen(!menuOpen)}
                            className="lg:hidden p-2 text-secondary-400 hover:text-white rounded-lg hover:bg-secondary-800"
                            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                            aria-expanded={menuOpen}
                            aria-controls="mobile-menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                {menuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {menuOpen && (
                    <div id="mobile-menu" className="lg:hidden pb-4 border-t border-secondary-700 mt-2 pt-3" role="menu">
                        <div className="flex flex-col gap-1">
                            <a
                                href="https://forms.gle/2GvpB888G5xRci1S9"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-3 bg-yellow-400 text-primary-950 font-bold rounded-lg mb-2 flex items-center justify-between text-center"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span>📝 Complete Google Signup Form</span>
                                <span>↗</span>
                            </a>
                            {mainTabs.map(tab => (
                                <Link
                                    key={tab.to}
                                    to={tab.to}
                                    role="menuitem"
                                    className={`px-4 py-2.5 rounded-lg text-base text-secondary-300 hover:bg-secondary-800 hover:text-white ${location.pathname === tab.to ? 'bg-primary-500 text-white font-semibold' : ''}`}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {tab.label}
                                </Link>
                            ))}
                            <div className="border-t border-secondary-700 my-2" role="separator" aria-hidden="true" />
                            {moreLinks.map(link => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    role="menuitem"
                                    className="px-4 py-2 rounded-lg text-sm text-secondary-400 hover:bg-secondary-800 hover:text-white"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
}

